import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TimeoutError, firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NetworkConstant } from './network-constant';
import { HeaderConstant } from './header-constant';
import { ApiKeyCacheService } from './api-key-cache.service';
import { ApiError, MissingApiKeyError } from './api-error';
import { LocalStorageManagerService } from '../storage/local-storage-manager.service';
import { AppConfigStore } from '../app-config/app-config.store';
import { TokenStoreService } from '../auth/token-store.service';
import { AuthStore } from '../store/auth.store';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { logAuthSession } from '../auth/auth-session-log';
import { LoggerService } from '../logger/logger.service';
import { PWA_DEVICE_INFO } from '../../modules/auth/viewmodel/login-request-builder';
import { RETURN_URL_QUERY, ROUTES, sanitizeReturnUrl } from '../routes';

const TAG = 'TokenRefresh';

/** Refresh slightly before access expiry so cold start / resume do not race the clock. */
const ACCESS_TOKEN_SKEW_MS = 60_000;

/** Cross-tab forced logout (AC9). Relevant when `blockBrowserAccess` is false. */
const AUTH_LOGOUT_CHANNEL = 'jiopc-auth-logout';

/**
 * Web Lock serialising refresh across every window of this origin. The backend rotates
 * `refToken` on each refresh, so two windows refreshing with the same token means the second
 * presents a rotated-away token, is rejected, and its logout (`clearAll()` on shared storage)
 * takes the healthy window down with it. Inside the lock each window re-reads storage first and
 * adopts a rotation another window already made — see `performRefresh`.
 */
const REFRESH_LOCK = 'jiopc-token-refresh';

/**
 * Deadline for the refresh POST (CF-02). Browsers put no timeout on XHR, so a half-open
 * connection used to leave the refresh pending for minutes — and with it every 401 retry
 * (they share `inFlight`), startup (it awaits the gate), and, under REFRESH_LOCK, every
 * other window. A timeout is a soft failure: the session is kept and the lock released.
 */
const REFRESH_TIMEOUT_MS = 15_000;

/**
 * Cool-down after a soft refresh failure (CF-08): first 30s, doubling to 5 min. During it,
 * background triggers (401 retries, the expiry timer) fail fast with the last soft error
 * instead of re-hitting a backend that is already failing — polling screens otherwise fire a
 * refresh per poll. User-driven triggers (startup, resume) always go through.
 */
const SOFT_FAILURE_COOLDOWN_MIN_MS = 30_000;
const SOFT_FAILURE_COOLDOWN_MAX_MS = 5 * 60_000;

/** setTimeout overflows above 2^31-1 ms (~24.8 days) and fires immediately (CF-07). */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

interface RefreshTokenResponse {
  jwt?: string;
  refToken?: string;
  ssoToken?: string;
  jwt_expires_on?: string;
  reftoken_expires_on?: string;
  success?: boolean;
  message?: string;
  customCode?: number;
}

export type SessionValidateResult =
  'valid' | 'refreshed' | 'logged_out' | 'anonymous' | 'offline_kept' | 'suspended';

export type SessionValidateTrigger = 'startup' | 'resume' | 'expiry-timer';

type RefreshTrigger = SessionValidateTrigger | '401-retry';

type LogoutMessage = { type: 'LOGOUT' };

/**
 * A refresh this window deliberately did not complete: the window was suspended (it became a
 * gated browser tab), or the user logged out / a new login happened while the request was in
 * flight, so its response belongs to a session that no longer exists. Never a reason to log out.
 */
export class RefreshCancelledError extends Error {
  constructor(reason: 'suspended' | 'session-changed') {
    super(`Token refresh cancelled: ${reason}`);
    this.name = 'RefreshCancelledError';
  }
}

/**
 * Should this refresh failure end the session? Shared by the session gate below and the
 * 401-retry path in response.interceptor.ts, which used to disagree.
 *
 * Soft — keep the session and try again later: no network, timeout, 408, 429, any 5xx, the CMS
 * API key not loaded yet, or a cancelled refresh. None of these say the refresh token is bad.
 * Everything else (400/401/403/412 auth rejections, `success:false`, no refresh token) is final.
 */
export function isSoftRefreshFailure(err: unknown): boolean {
  if (err instanceof ApiError || err instanceof HttpErrorResponse) {
    const { status } = err;
    return status === 0 || status === 408 || status === 429 || (status >= 500 && status < 600);
  }
  return (
    err instanceof TimeoutError ||
    err instanceof MissingApiKeyError ||
    err instanceof RefreshCancelledError
  );
}

/**
 * Real HTTP-backed token refresh, single-flighted across concurrent 401s —
 * Angular-native port of Consumer's network/ResponseInterceptor.ts
 * `refreshAccessToken`.
 *
 * Also owns the session gate (WI 39443):
 * - Cold start before `AuthStore.hydrate()` (AC1–AC3, AC6, AC8)
 * - Resume on `visibilitychange` / `pageshow` (AC4/AC5)
 * - Idle-while-visible timer at stored refresh expiry (proactive login redirect)
 *
 * DEVIATION FROM CONSUMER (a deliberate improvement, not just a copy — see
 * project instructions to apply Angular/RxJS best practices where the
 * original has a gap): Consumer's axios interceptor calls
 * `refreshAccessToken()` independently for every 401'd request with no
 * de-duplication, so N concurrent 401s fire N concurrent refresh calls
 * against the backend. This service instead shares one in-flight refresh
 * `Promise` across all concurrent callers (a standard Angular interceptor
 * pattern), so only one refresh request is ever in flight at a time. The
 * request/response shape and storage side effects are otherwise identical.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
  private http = inject(HttpClient);
  private apiKeyCache = inject(ApiKeyCacheService);
  private localStorageManager = inject(LocalStorageManagerService);
  private appConfigStore = inject(AppConfigStore);
  private tokenStore = inject(TokenStoreService);
  private authStore = inject(AuthStore);
  private connectivity = inject(ConnectivityService);
  private router = inject(Router);
  private logger = inject(LoggerService);

  private inFlight: Promise<string> | null = null;
  private validateInFlight: Promise<SessionValidateResult> | null = null;
  private forceLogoutInFlight: Promise<void> | null = null;
  private logoutChannel: BroadcastChannel | null = null;
  private ignoreBroadcast = false;
  private sessionLifecycleArmed = false;
  private suspended = false;
  private softFailureUntil = 0;
  private softFailureCooldownMs = 0;
  private lastSoftFailure: unknown = null;

  /**
   * True while the session can't be renewed for a transient reason (timeout, 5xx, CMS key):
   * the user still looks signed in but authenticated calls fail (CF-09). app.ts shows a
   * one-time notice per outage. Cleared by any successful refresh or by sign-out.
   */
  readonly sessionDegraded = signal(false);
  private refreshExpiryTimerId: ReturnType<typeof setTimeout> | null = null;
  private readonly onVisibilityForSession = (): void => {
    if (document.visibilityState !== 'visible') return;
    void this.validateSession('resume');
  };
  private readonly onPageShowForSession = (event: PageTransitionEvent): void => {
    // bfcache restore — treat like resume even when visibility already "visible".
    if (event.persisted || document.visibilityState === 'visible') {
      void this.validateSession('resume');
    }
  };

  /** Call once from app bootstrap — other tabs clear session when this tab force-logs out. */
  initMultiTabLogout(): void {
    if (typeof BroadcastChannel === 'undefined' || this.logoutChannel) return;
    this.logoutChannel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
    this.logoutChannel.onmessage = (event: MessageEvent<LogoutMessage>) => {
      if (event.data?.type !== 'LOGOUT' || this.ignoreBroadcast) return;
      logAuthSession('session-gate-multitab-logout', 'warn');
      this.clearRefreshExpiryWatch();
      this.resetSoftFailureCooldown();
      this.authStore.logout();
      void this.router.navigateByUrl(`/${ROUTES.LOGIN}`, { replaceUrl: true });
    };
  }

  /**
   * Resume + idle expiry watch (AC4/AC5). Call once after cold-start validate.
   * Uses `visibilitychange` + `pageshow` (not `focus` — too noisy).
   */
  initSessionLifecycle(): void {
    if (this.sessionLifecycleArmed || typeof document === 'undefined') return;
    this.sessionLifecycleArmed = true;
    document.addEventListener('visibilitychange', this.onVisibilityForSession);
    window.addEventListener('pageshow', this.onPageShowForSession);
    this.scheduleRefreshExpiryWatch();
  }

  /**
   * Permanently stop this window from validating, refreshing, or logging out. Called when a
   * running PWA window turns into a gated browser tab (`App.reapplyBrowserGate`): it shows the
   * block screen but its resume listeners, expiry timer, and in-memory tokens would otherwise
   * stay live, and a refresh from that stale copy could log out the real PWA window. Leaving
   * the gate reloads the page, which gives a fresh, un-suspended service.
   */
  suspendSessionLifecycle(): void {
    this.suspended = true;
    this.clearRefreshExpiryWatch();
    if (this.sessionLifecycleArmed && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityForSession);
      window.removeEventListener('pageshow', this.onPageShowForSession);
      this.sessionLifecycleArmed = false;
    }
    logAuthSession('session-gate-suspended', 'info');
  }

  /**
   * Disarms what `initSessionLifecycle()` / `initMultiTabLogout()` set up. In the
   * app this only runs when the root injector is destroyed, but TestBed destroys
   * it after every test: without this, every spec that boots `App` left its
   * document/window listeners, expiry timer and BroadcastChannel behind, so
   * later specs' `visibilitychange` events and logout broadcasts reached stale
   * services from earlier spec files.
   */
  ngOnDestroy(): void {
    if (this.sessionLifecycleArmed) {
      document.removeEventListener('visibilitychange', this.onVisibilityForSession);
      window.removeEventListener('pageshow', this.onPageShowForSession);
      this.sessionLifecycleArmed = false;
    }
    this.clearRefreshExpiryWatch();
    this.logoutChannel?.close();
    this.logoutChannel = null;
  }

  /**
   * Auth gate (AC1–AC3, AC4/AC5, AC6, AC8). Startup runs before hydrate;
   * resume / expiry-timer re-run the same rules without mounting authenticated UI on failure.
   */
  validateSession(trigger: SessionValidateTrigger = 'startup'): Promise<SessionValidateResult> {
    if (this.suspended) return Promise.resolve('suspended');
    if (this.validateInFlight) return this.validateInFlight;
    this.validateInFlight = this.runValidateSession(trigger).finally(() => {
      this.validateInFlight = null;
    });
    return this.validateInFlight;
  }

  /** Attempts a single (de-duplicated) token refresh. Resolves with the new JWT, or rejects on failure. */
  refresh(trigger: RefreshTrigger = '401-retry'): Promise<string> {
    if (this.suspended) return Promise.reject(new RefreshCancelledError('suspended'));
    if (this.inFlight) {
      return this.inFlight;
    }
    const userDriven = trigger === 'startup' || trigger === 'resume';
    if (!userDriven && Date.now() < this.softFailureUntil) {
      logAuthSession('refresh-skipped-cooldown', 'debug', { trigger });
      return Promise.reject(this.lastSoftFailure);
    }

    this.inFlight = this.withRefreshLock(() => this.performRefresh(trigger))
      .then(
        (jwt) => {
          this.resetSoftFailureCooldown();
          return jwt;
        },
        (err: unknown) => {
          this.recordSoftFailure(err);
          throw err;
        },
      )
      .finally(() => {
        this.inFlight = null;
      });

    return this.inFlight;
  }

  /**
   * Clear auth, broadcast to other tabs, and route to login.
   * `returnUrl` (or the current router URL) preserves a deep link after re-auth (AC7).
   * Single-flighted so concurrent 401s do not stack navigations.
   */
  forceLogout(returnUrl?: string | null): Promise<void> {
    if (this.forceLogoutInFlight) return this.forceLogoutInFlight;
    this.forceLogoutInFlight = this.runForceLogout(returnUrl).finally(() => {
      this.forceLogoutInFlight = null;
    });
    return this.forceLogoutInFlight;
  }

  /**
   * User-initiated sign-out: clear this window's session and tell every other window (CF-11).
   * No navigation — the caller decides where to go. Manual logout used to call
   * `AuthStore.logout()` directly, which skipped the broadcast, so a second PWA window stayed
   * signed in on its in-memory JWT.
   */
  signOut(): void {
    this.clearRefreshExpiryWatch();
    this.resetSoftFailureCooldown();
    this.authStore.logout();
    this.broadcastLogout();
  }

  private async runForceLogout(returnUrl?: string | null): Promise<void> {
    this.clearRefreshExpiryWatch();
    this.resetSoftFailureCooldown();
    const safeReturn = sanitizeReturnUrl(returnUrl?.trim() || this.router.url);
    this.authStore.logout();
    this.broadcastLogout();

    const tree = safeReturn
      ? this.router.createUrlTree([`/${ROUTES.LOGIN}`], {
          queryParams: { [RETURN_URL_QUERY]: safeReturn },
        })
      : this.router.createUrlTree([`/${ROUTES.LOGIN}`]);

    await this.router.navigateByUrl(tree, { replaceUrl: true });
  }

  /**
   * Gate-initiated logout. Before auth has hydrated the navigation must NOT be awaited (CF-01):
   * `/login`'s guestGuard awaits hydration, which app.ts only performs after the startup gate
   * returns — and `AuthStore.logout()` deliberately keeps `isLoading` latched during bootstrap.
   * Awaiting it deadlocked the app on its loader. Keyed on hydration, not the trigger: a `resume`
   * (tab refocused during the startup CMS wait) can end the session before hydration too. The
   * session is cleared synchronously either way; the navigation completes once app.ts hydrates.
   */
  private async endSession(): Promise<void> {
    const logout = this.forceLogout(this.router.url);
    if (!this.authStore.isLoading()) {
      await logout;
      return;
    }
    logout.catch((err: unknown) =>
      this.logger.warn(TAG, 'Pre-hydration logout navigation failed', err),
    );
  }

  private async runValidateSession(
    trigger: SessionValidateTrigger,
  ): Promise<SessionValidateResult> {
    logAuthSession('session-gate-invoked', 'info', { trigger });

    const accessToken = this.localStorageManager.getAccessToken()?.trim() || '';
    const refreshToken = this.localStorageManager.getRefreshToken()?.trim() || '';

    if (!accessToken && !refreshToken) {
      this.clearRefreshExpiryWatch();
      logAuthSession('session-gate-anonymous', 'info', { trigger });
      return 'anonymous';
    }

    // Local refresh expiry is enough to force logout — no useless refresh API call.
    if (this.localStorageManager.isRefreshTokenExpired()) {
      logAuthSession('session-gate-forced-logout', 'warn', {
        trigger,
        reason: 'refresh_token_expired_locally',
      });
      await this.endSession();
      return 'logged_out';
    }

    const needsRefresh =
      !accessToken || this.localStorageManager.isAccessTokenExpired(ACCESS_TOKEN_SKEW_MS);

    if (!needsRefresh) {
      logAuthSession('session-gate-valid', 'info', { trigger });
      this.scheduleRefreshExpiryWatch();
      return 'valid';
    }

    if (!refreshToken) {
      logAuthSession('session-gate-forced-logout', 'warn', {
        trigger,
        reason: 'missing_refresh_token',
      });
      await this.endSession();
      return 'logged_out';
    }

    try {
      await this.refresh(trigger);
      logAuthSession('session-gate-refresh-succeeded', 'info', { trigger });
      this.scheduleRefreshExpiryWatch();
      return 'refreshed';
    } catch (err) {
      // Network / timeout / 5xx / missing CMS key: keep session (AC8). Only an explicit auth
      // rejection logs out — see isSoftRefreshFailure (shared with response.interceptor.ts).
      if (isSoftRefreshFailure(err)) {
        if (this.isNetworkFailure(err)) this.connectivity.reportNetworkError();
        logAuthSession('session-gate-offline-kept', 'warn', {
          trigger,
          reason: this.failureReason(err),
        });
        // Keep watching — when connectivity returns, resume / next timer can re-check.
        this.scheduleRefreshExpiryWatch();
        return 'offline_kept';
      }

      logAuthSession('session-gate-forced-logout', 'warn', {
        trigger,
        reason: this.failureReason(err),
      });
      await this.endSession();
      return 'logged_out';
    }
  }

  /** Fire validate when stored refresh expiry elapses while the tab stays visible. */
  scheduleRefreshExpiryWatch(): void {
    this.clearRefreshExpiryWatch();
    // Backend contract: ISO-8601 UTC (`2024-12-31T23:59:59Z`), which Date.parse reads everywhere.
    const expMs = Date.parse(this.localStorageManager.getRefreshTokenExpiry() ?? '');
    if (Number.isNaN(expMs)) return;

    const delayMs = Math.min(Math.max(0, expMs - Date.now()), MAX_TIMER_DELAY_MS);
    logAuthSession('session-gate-expiry-watch-armed', 'debug', { delayMs });
    this.refreshExpiryTimerId = setTimeout(() => {
      this.refreshExpiryTimerId = null;
      void this.validateSession('expiry-timer');
    }, delayMs);
  }

  private clearRefreshExpiryWatch(): void {
    if (this.refreshExpiryTimerId === null) return;
    clearTimeout(this.refreshExpiryTimerId);
    this.refreshExpiryTimerId = null;
  }

  private recordSoftFailure(err: unknown): void {
    // Only failures that say "the backend/network is unwell" arm the cool-down.
    if (!isSoftRefreshFailure(err) || err instanceof RefreshCancelledError) return;
    this.softFailureCooldownMs = Math.min(
      this.softFailureCooldownMs ? this.softFailureCooldownMs * 2 : SOFT_FAILURE_COOLDOWN_MIN_MS,
      SOFT_FAILURE_COOLDOWN_MAX_MS,
    );
    this.softFailureUntil = Date.now() + this.softFailureCooldownMs;
    this.lastSoftFailure = err;
    this.sessionDegraded.set(true);
  }

  private resetSoftFailureCooldown(): void {
    this.softFailureUntil = 0;
    this.softFailureCooldownMs = 0;
    this.lastSoftFailure = null;
    this.sessionDegraded.set(false);
  }

  /** Runs `fn` under the cross-window refresh lock; falls back to running it directly. */
  private withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (!locks?.request) return fn();
    let ran = false;
    return (
      locks.request(REFRESH_LOCK, () => {
        ran = true;
        return fn();
      }) as Promise<T>
    ).catch((err: unknown) => {
      if (ran) throw err; // fn's own failure — classify it as usual
      // The lock itself was refused (e.g. SecurityError, document not fully active): refresh
      // without cross-window coordination rather than turning it into a logout.
      this.logger.warn(TAG, 'Refresh lock unavailable — refreshing without it', err);
      return fn();
    });
  }

  private async performRefresh(trigger: RefreshTrigger): Promise<string> {
    // Captured before any await: a logout / new login in between makes this refresh moot
    // (CF-06). Checked again after every await below, so the old refresh token is never posted
    // and the old session's tokens are never written back.
    const epoch = this.authStore.sessionEpoch;
    const ensureSameSession = (): void => {
      if (this.authStore.sessionEpoch === epoch) return;
      logAuthSession('refresh-discarded-session-changed', 'warn', { trigger });
      throw new RefreshCancelledError('session-changed');
    };

    // Another window may have rotated the session while we held the lock-free stale copy.
    const cachedRefreshToken = this.localStorageManager.getRefreshToken();
    await this.localStorageManager.reloadSessionTokens();
    ensureSameSession();
    const refreshToken = this.localStorageManager.getRefreshToken();
    if (refreshToken && refreshToken !== cachedRefreshToken) {
      const adoptedJwt = this.localStorageManager.getAccessToken();
      if (adoptedJwt && !this.localStorageManager.isAccessTokenExpired(ACCESS_TOKEN_SKEW_MS)) {
        logAuthSession('refresh-adopted-from-other-window', 'info', { trigger });
        this.authStore.updateAccessToken(adoptedJwt);
        this.scheduleRefreshExpiryWatch();
        return adoptedJwt;
      }
    }
    if (!refreshToken) {
      logAuthSession('refresh-aborted-no-ref-token', 'warn', { trigger });
      throw new Error('No refresh token');
    }

    logAuthSession('refresh-request-start', 'info', { trigger });

    // The client API key comes from the CMS config (AppConfigStore persists it on every fetch).
    // A key saved by an earlier launch is used straight away instead of waiting on this
    // launch's CMS fetch — on a slow CDN that wait was up to APP_CONFIG_READY_TIMEOUT_MS more on
    // the cold-start critical path (docs/cold-start-optimization-plan.md B6). Only with no saved
    // key (first launch) do we wait — bounded, see waitReady() — on the same de-duped fetch.
    // The saved key can be one CMS has since rotated: if the API rejects the refresh outright,
    // it is retried once with the fresh key below before the rejection can end the session.
    let encryptedClientApiKey = this.localStorageManager.getClientApiKey();
    const usedSavedKey = !!encryptedClientApiKey;
    if (!encryptedClientApiKey) {
      await this.appConfigStore.waitReady();
      encryptedClientApiKey = this.localStorageManager.getClientApiKey();
    }
    if (!encryptedClientApiKey) {
      logAuthSession('refresh-aborted-missing-api-key', 'warn', { trigger });
      throw new MissingApiKeyError('cannot attempt token refresh');
    }

    let response: RefreshTokenResponse;
    try {
      response = await this.postRefresh(encryptedClientApiKey, refreshToken, ensureSameSession);
    } catch (err) {
      ensureSameSession();
      const freshKey = usedSavedKey && !isSoftRefreshFailure(err)
        ? await this.rotatedClientApiKey(encryptedClientApiKey)
        : null;
      if (!freshKey) {
        this.logRefreshFailure(trigger, err);
        throw err;
      }
      logAuthSession('refresh-retry-rotated-api-key', 'warn', { trigger });
      try {
        response = await this.postRefresh(freshKey, refreshToken, ensureSameSession);
      } catch (retryErr) {
        ensureSameSession();
        this.logRefreshFailure(trigger, retryErr);
        throw retryErr;
      }
    }

    ensureSameSession();

    const { jwt, refToken, ssoToken, jwt_expires_on, reftoken_expires_on, success, message } =
      response;

    if (success === false || !jwt) {
      this.logger.warn(TAG, 'Refresh rejected by API', { trigger, success, message });
      logAuthSession('refresh-rejected-by-api', 'warn', { trigger, success, message });
      throw new Error(message || 'Token refresh rejected');
    }

    this.localStorageManager.setAccessToken(jwt);
    if (refToken) this.localStorageManager.setRefreshToken(refToken);
    if (ssoToken) this.localStorageManager.setSsoToken(ssoToken);
    // Expiries only after the (async, encrypted) token writes land: a window killed in between
    // must not pair the new deadline with the rotated-away refresh token.
    await this.localStorageManager.flushPendingWrites();
    ensureSameSession();
    if (jwt_expires_on) this.localStorageManager.setTokenExpiry(jwt_expires_on);
    // Sliding session: every refresh extends the deadline to whatever the API returns. If the
    // token rotated but no deadline came back, the stored one belongs to the dead token —
    // drop it so the server, not a stale local clock check, decides when the session ends.
    if (reftoken_expires_on) {
      this.localStorageManager.setRefreshTokenExpiry(reftoken_expires_on);
    } else if (refToken && refToken !== refreshToken) {
      logAuthSession('refresh-rotated-without-expiry', 'warn', { trigger });
      this.localStorageManager.clearRefreshTokenExpiry();
    }

    this.authStore.updateAccessToken(jwt);
    logAuthSession('refresh-request-succeeded', 'info', { trigger });
    this.scheduleRefreshExpiryWatch();
    return jwt;
  }

  /** The refresh call itself, with `encryptedClientApiKey` as its `x-api-key`. */
  private async postRefresh(
    encryptedClientApiKey: string,
    refreshToken: string,
    ensureSameSession: () => void,
  ): Promise<RefreshTokenResponse> {
    const clientApiKey = await this.apiKeyCache.getDecryptedKey(encryptedClientApiKey);
    ensureSameSession();
    return firstValueFrom(
      this.http
        .post<RefreshTokenResponse>(
          `${environment.envDomainUrl}${NetworkConstant.REFRESH_ACCESS_TOKEN}`,
          {
            jwt: this.tokenStore.getAccessToken() ?? '',
            refToken: refreshToken,
            ssoToken: this.localStorageManager.getSsoToken() ?? '',
            deviceInfo: PWA_DEVICE_INFO,
          },
          {
            headers: {
              [HeaderConstant.HEADER_CONTENT_TYPE]: HeaderConstant.CONTENT_TYPE_JSON,
              [HeaderConstant.HEADER_USER_X_API_KEY]: clientApiKey,
            },
          },
        )
        .pipe(timeout(REFRESH_TIMEOUT_MS)),
    );
  }

  /**
   * After a refresh sent with a saved key was rejected: waits (bounded) for this launch's CMS
   * fetch and returns its key if it differs from the one sent — i.e. CMS rotated it — else null.
   */
  private async rotatedClientApiKey(sentKey: string): Promise<string | null> {
    await this.appConfigStore.waitReady();
    const current = this.localStorageManager.getClientApiKey();
    return current && current !== sentKey ? current : null;
  }

  private logRefreshFailure(trigger: RefreshTrigger, err: unknown): void {
    this.logger.error(TAG, 'Refresh HTTP error', err);
    logAuthSession('refresh-request-failed', 'warn', {
      trigger,
      error: err instanceof Error ? err.message : String(err),
    });
  }

  private broadcastLogout(): void {
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this.ignoreBroadcast = true;
      const channel = this.logoutChannel ?? new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
      channel.postMessage({ type: 'LOGOUT' } satisfies LogoutMessage);
      if (!this.logoutChannel) channel.close();
    } catch {
      // Broadcast unavailable — same-tab logout still applies.
    } finally {
      queueMicrotask(() => {
        this.ignoreBroadcast = false;
      });
    }
  }

  private isNetworkFailure(err: unknown): boolean {
    if (err instanceof ApiError) return err.isNetworkError;
    if (err instanceof HttpErrorResponse) return err.status === 0;
    return false;
  }

  private failureReason(err: unknown): string {
    if (err instanceof ApiError) return err.code || `http_${err.status}`;
    if (err instanceof HttpErrorResponse) {
      return err.status === 0 ? 'ERR_NETWORK' : `http_${err.status}`;
    }
    if (err instanceof MissingApiKeyError) return 'MISSING_API_KEY';
    if (err instanceof Error) return err.message || 'unknown';
    return 'unknown';
  }
}
